import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  IconBuilding,
  IconMail,
  IconMapPin,
  IconBrandInstagram,
  IconWorld,
  IconDeviceFloppy,
  IconLoader2,
} from "@tabler/icons-react";
import { toast } from "sonner";
import { getAgencyProfile, updateAgencyProfile, type AgencyProfile } from "../api";

export function ProfileTab() {
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useQuery({
    queryKey: ["agency-profile"],
    queryFn: getAgencyProfile,
  });

  const updateMutation = useMutation({
    mutationFn: updateAgencyProfile,
    onSuccess: (updated) => {
      queryClient.setQueryData(["agency-profile"], updated);
      toast.success("Perfil atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar perfil.");
    },
  });

  const { register, handleSubmit } = useForm<AgencyProfile>({
    values: profile, // Preenche o form quando os dados chegam
  });

  const onSubmit = (data: AgencyProfile) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Carregando perfil...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={updateMutation.isPending}
          className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-700 transition-colors disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <IconLoader2 className="animate-spin" size={20} />
          ) : (
            <IconDeviceFloppy size={20} />
          )}
          Salvar Alterações
        </button>
      </div>

      <div className="grid gap-6">
        {/* BASIC INFO */}
        <section className="bg-white rounded-xl border p-6 shadow-sm space-y-4">
          <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
            <IconBuilding size={20} className="text-brand-600" />
            Informações Básicas
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Nome Fantasia</label>
              <input {...register("name")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Razão Social</label>
              <input {...register("legalName")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">CNPJ</label>
              <input {...register("cnpj")} disabled className="w-full rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-500" />
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section className="bg-white rounded-xl border p-6 shadow-sm space-y-4">
          <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
            <IconMail size={20} className="text-brand-600" />
            Contato & Social
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">E-mail Comercial</label>
              <input {...register("email")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Telefone</label>
              <input {...register("phone")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <IconBrandInstagram size={16} /> Instagram
              </label>
              <input {...register("social.instagram")} placeholder="@seuinsta" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <IconWorld size={16} /> Site
              </label>
              <input {...register("social.website")} placeholder="www.suaimob.com.br" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>
        </section>

        {/* ADDRESS */}
        <section className="bg-white rounded-xl border p-6 shadow-sm space-y-4">
          <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2">
            <IconMapPin size={20} className="text-brand-600" />
            Endereço
          </h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Logradouro</label>
              <input {...register("address.street")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Número</label>
              <input {...register("address.number")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Bairro</label>
              <input {...register("address.neighborhood")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Cidade</label>
              <input {...register("address.city")} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Estado (UF)</label>
              <input {...register("address.state")} maxLength={2} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 uppercase" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
